import React, { useState, useMemo, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { useSaveTag } from '@frontend/app/hooks';
import { useMessagingContext } from '@frontend/hooks';
import { Modal, Empty } from '@frontend/app/refresh-components';
import { P } from '@frontend/shadcn/components/typography/p';
import { MagnifyingGlassIcon, TagsIcon } from '@revfluence/fresh-icons/regular/esm';
import { LoadSpinner } from '@components';
import { getUniqueRefreshColor } from '@frontend/utils';
import { Input } from '@/shadcn/components/ui/input';
import { Checkbox } from '@/shadcn/components/ui/checkbox';
import { Badge } from '@/shadcn/components/ui/badge';
import { Dialog, DialogContent } from '@/shadcn/components/ui/dialog';
import { Button } from '@/shadcn/components/ui/button';

interface TagOption {
  value: string | number;
  displayName: string;
}

interface SelectedTag {
  fieldId: string | number;
  fieldValue: string;
}

interface TagSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (selectedTags: SelectedTag[]) => void;
  availableTags: TagOption[];
  initialSelectedTags?: SelectedTag[];
  onRefreshTags?: () => void; // Callback to refresh tags from parent
}

// Tag item component
interface TagItemProps {
  tag: TagOption;
  isSelected: boolean;
  onToggle: () => void;
  disabled?: boolean;
}

const TagItem: React.FC<TagItemProps> = ({ tag, isSelected, onToggle, disabled }) => {
  return (
    <div className="flex justify-between">
      <div className="flex gap-2 items-center">
        <Checkbox
          id={`tag-${tag.value}`}
          checked={isSelected}
          disabled={disabled}
          onCheckedChange={onToggle}
        />
        <Badge
          variant="secondary"
          className={`${getUniqueRefreshColor(tag.displayName)} hover:${getUniqueRefreshColor(tag.displayName)} px-1 font-regular`}
          title={tag.displayName}
        >
          <span className="truncate max-w-[200px]">
            {tag.displayName}
          </span>
        </Badge>
      </div>
    </div>
  );
};

export const TagSelector: React.FC<TagSelectorProps> = ({
  isOpen,
  onClose,
  onSave,
  availableTags,
  initialSelectedTags = [],
  onRefreshTags,
}) => {
  const [search, setSearch] = useState('');
  const [selectedTags, setSelectedTags] = useState<SelectedTag[]>(initialSelectedTags);
  const [isCreatingTag, setIsCreatingTag] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [isNewTagModalOpen, setIsNewTagModalOpen] = useState(false);

  const { showMessage } = useMessagingContext();

  const [saveTag] = useSaveTag({
    onCompleted: ({ tag }) => {
      // Add the newly created tag to selected tags
      setSelectedTags((prev) => [...prev, {
        fieldId: tag.id,
        fieldValue: tag.name,
      }]);
      setIsCreatingTag(false);
      // Refresh the available tags list
      if (onRefreshTags) {
        onRefreshTags();
      }
      showMessage({
        type: 'success',
        content: `Tag "${tag.name}" created successfully`,
      });
    },
    onError: (err) => {
      showMessage({
        type: 'error',
        content: err.message || 'Failed to create tag',
      });
      setIsCreatingTag(false);
    },
  });

  // Only initialize selectedTags when modal first opens, not on every re-render
  const [modalOpenCount, setModalOpenCount] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setModalOpenCount((prev) => prev + 1);
      setSearch('');
    }
  }, [isOpen]);

  // Only reset selected tags when modal opens for the first time in this session
  useEffect(() => {
    if (modalOpenCount === 1) {
      setSelectedTags(initialSelectedTags);
    }
  }, [modalOpenCount, initialSelectedTags]);

  // Use available tags directly since new tags are handled via API
  const allTags = useMemo(() => availableTags, [availableTags]);

  // Filter tags based on search
  const filteredTags = useMemo(() => {
    if (!search) return allTags;
    return allTags.filter((tag) =>
      tag.displayName.toLowerCase().includes(search.toLowerCase())
    );
  }, [allTags, search]);

  // Check if a tag is selected
  const isTagSelected = (tag: TagOption): boolean => {
    return selectedTags.some((selected) =>
      selected.fieldValue === tag.displayName
    );
  };

  // Toggle tag selection
  const toggleTag = (tag: TagOption) => {
    const isCurrentlySelected = isTagSelected(tag);

    if (isCurrentlySelected) {
      // Remove tag
      setSelectedTags((prev) =>
        prev.filter((selected) => selected.fieldValue !== tag.displayName)
      );
    } else {
      // Add tag
      setSelectedTags((prev) => [...prev, {
        fieldId: tag.value,
        fieldValue: tag.displayName,
      }]);
    }
  };

  // Create new tag via API (similar to ModifyTagMembersModal)
  const handleCreateNewTag = async () => {
    const trimmedSearch = search.trim();
    if (trimmedSearch) {
      setIsCreatingTag(true);
      await saveTag({
        variables: {
          tag: {
            name: trimmedSearch,
          },
        },
      });
      setSearch('');
    }
  };

  // Create new tag from modal input
  const handleCreateNewTagFromModal = async () => {
    const trimmedName = newTagName.trim();
    if (trimmedName) {
      setIsCreatingTag(true);
      await saveTag({
        variables: {
          tag: {
            name: trimmedName,
          },
        },
      });
      setNewTagName('');
      setIsNewTagModalOpen(false);
    }
  };

  // Show "New Tag" button - when there's a search term and no exact match in filtered tags
  const showNewTagButton = useMemo(() => {
    const trimmedSearch = search.trim();
    if (!trimmedSearch) return false;

    // Check if search term exactly matches any existing tag
    const exactMatch = filteredTags.some((tag) =>
      tag.displayName.toLowerCase() === trimmedSearch.toLowerCase()
    );

    return !exactMatch;
  }, [search, filteredTags]);

  // Handle save
  const handleSave = () => {
    onSave(selectedTags);
    onClose();
  };

  // Check if selection has changed
  const hasSelectionChanged = useMemo(() => {
    if (selectedTags.length !== initialSelectedTags.length) return true;

    return selectedTags.some((selected) =>
      !initialSelectedTags.some((initial) =>
        initial.fieldValue === selected.fieldValue
      )
    );
  }, [selectedTags, initialSelectedTags]);

  // Check if loading
  const isLoading = false; // You can add actual loading state if needed

  const loadingContent = (
    <div className="p-8 bg-secondary h-full flex">
      <LoadSpinner />
    </div>
  );

  const mainContent = (
    <div>
      {filteredTags.length === 0 ? (
        <div className="py-2">
          <Empty
            icon={TagsIcon}
            title="No tags"
            description="Modify your search or create a new tag."
            primaryCtaProps={{
              text: 'Create New Tag',
              onClick: handleCreateNewTag,
            }}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {filteredTags.map((tag) => (
            <TagItem
              key={`${tag.value}-${tag.displayName}`}
              tag={tag}
              isSelected={isTagSelected(tag)}
              onToggle={() => toggleTag(tag)}
              disabled={false}
            />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <>
      <Modal
        isOpen={isOpen && !isNewTagModalOpen}
        onClose={onClose}
        title="Select Tags"
        onOk={hasSelectionChanged && !isCreatingTag ? handleSave : undefined}
        okText="Select"
        cancelText="Cancel"
      >
        <div className="flex flex-col gap-3 max-h-[400px]">
          <P className="m-0">
            Assign selected tags to members.
          </P>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              type="search"
              placeholder="Search tags..."
              className="w-full rounded-lg bg-background pl-8"
            />
          </div>
          <div className="flex flex-col gap-2 overflow-y-auto" style={{ maxHeight: '200px' }}>
            {isLoading ? loadingContent : mainContent}
          </div>

          {/* Show new tag button when there's a search term */}
          {showNewTagButton && (
            <div className="w-full">
              <Button
                variant="outline"
                onClick={handleCreateNewTag}
                disabled={isCreatingTag}
                className="w-full justify-center gap-2"
                title={isCreatingTag ? 'Creating Tag...' : `Create "${search.trim()}"`}
              >
                <FontAwesomeIcon icon={faPlus} className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">
                  {isCreatingTag ? 'Creating Tag...' : `Create "${search.trim()}"`}
                </span>
              </Button>
            </div>
          )}

          {/* Selection count */}
          <div className="text-xs text-muted-foreground text-center w-full">
            {selectedTags.length} tags selected
          </div>
        </div>
      </Modal>

      {/* New Tag Creation Modal */}
      <Dialog
        open={isNewTagModalOpen}
        onOpenChange={() => {
          setIsNewTagModalOpen(false);
          setNewTagName('');
        }}
      >
        <DialogContent className="max-w-md p-3">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Create New Tag</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tag Name
              </label>
              <Input
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                placeholder="Enter tag name"
                disabled={isCreatingTag}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newTagName.trim()) {
                    handleCreateNewTagFromModal();
                  }
                }}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setIsNewTagModalOpen(false);
                  setNewTagName('');
                }}
                disabled={isCreatingTag}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateNewTagFromModal}
                disabled={!newTagName.trim() || isCreatingTag}
                className="bg-gray-700 hover:bg-gray-800 text-white"
              >
                {isCreatingTag ? 'Creating...' : 'Create Tag'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
